// Stage 7A D1 — pure routing helpers for the Preview section.
//
// scrollToValidationField translates a validation issue's dot-path locator
// (e.g. 'adSet.targeting.ageMin') into a DOM anchor and scrolls/focuses it. The
// compose page only carries section-level anchors (#campaign / #adset / #ad)
// plus a handful of element ids, so the mapping is path-prefix → anchor with a
// small explicit override table for the fields that DO have a precise id.

// Explicit field-path → element-id overrides (fields that render with an id).
const EXPLICIT_FIELD_IDS: Record<string, string> = {
  accountId: 'campaign',
  'ads.creative.callToAction': 'ad-cta',
};

// Map a validation field dot-path to the compose anchor that contains it. Pure +
// total — unknown paths fall back to the Campaign section (the top of the form).
export function fieldPathToAnchor(fieldPath: string): string {
  const explicit = EXPLICIT_FIELD_IDS[fieldPath];
  if (explicit) return explicit;

  const head = fieldPath.split('.')[0];
  switch (head) {
    case 'accountId':
    case 'campaign':
      return 'campaign';
    case 'adSets': // multi-ad-set: per-ad-set fields live in the Ad Sets section
      return 'adset';
    case 'ads':
      return 'ad';
    default:
      return 'campaign';
  }
}

// Scroll the anchor for `fieldPath` into view and focus its first focusable
// control. DOM side-effecting (kept thin so the routing logic above stays pure
// and unit-testable). No-op when the element isn't mounted.
export function scrollToValidationField(fieldPath: string): void {
  if (typeof document === 'undefined') return;
  const id = fieldPathToAnchor(fieldPath);
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const focusable = el.querySelector<HTMLElement>(
    'input:not([type="hidden"]),select,textarea,button,[tabindex]',
  );
  focusable?.focus({ preventScroll: true });
}

// D2.17 — the Meta optimization goal we send with a reach estimate. The ad set
// already carries the chosen optimizationGoal; OFFSITE_CONVERSIONS isn't a valid
// delivery-estimate goal, so fall back to LINK_CLICKS for the estimate only (the
// estimate is advisory + currently gracefully hidden when Zernio rejects it).
export function reachOptimizationGoal(optimizationGoal: string): string {
  return optimizationGoal === 'OFFSITE_CONVERSIONS' ? 'LINK_CLICKS' : optimizationGoal;
}
